import React, {
  createRef,
  useEffect,
  useState,
  useContext,
  useCallback,
} from 'react';
import { onAdaReady } from '@pcf-engineering/gatsby-plugin-pcs-ada-chatbot';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { updateShowAddToCartTooltip } from '../../redux/reducers/cartReducer';
import useActiveStore from '../../hooks/useActiveStore';

import { StoreLocatorContext } from '../StoreLocator';
import { formatStoreName } from '../../utils/formatStoreName';
import useTmsApi from '../../hooks/useTmsApi';
import BladeContainer from '../UI/BladeContainer/BladeContainer';

import CartSuccessPhase from '../CartComponents/CartSuccessPhase/CartSuccessPhase';
import { DeviceType } from '../../types';
import { useFeatureFlag } from '../FeatureFlag/useFeatureFlag';
import { FeatureFlags } from '../FeatureFlag/featureFlags';
import { AllContentfulPhoneBrand } from '../../ContentfulTypes';
import { createSupportTicket } from './createSupportTicket';
import ReservePhase1 from './ReservePhase1/ReservePhase1';
import ReservePhase2 from './ReservePhase2/ReservePhase2';
import ReservePhase3 from './ReservePhase3/ReservePhase3';
import ReserveError from './ReserveError/ReserveError';
import CloseModal from './CloseModal/CloseModal';
import { Data, PrevPhaseButton, DeviceInfoWrapper } from './styles';
import { ISelectedCarrier } from './types';

interface Props {
  bladeVisible: boolean;
  setBladeVisible: (...args: any[]) => any;
  selectedStorageSize: string;
  phone: Record<string, any>;
  onColourChange: (...args: any[]) => any;
  colours: Record<string, any>[];
  selectedColour: string;
  selectSize: (...args: any[]) => any;
  storageOptions: string[];
  availableOffers: Record<string, any>[];
  reserveData: Record<string, any>;
  variantLabels: Record<string, any>;
  inventoryLabels?: Record<string, any>;
  page?: string;
  reserveStatus?: string;
  reservableCombos: Record<string, any>;
  phoneBrands: AllContentfulPhoneBrand;
}

const ReservePhoneBlade = ({
  bladeVisible,
  setBladeVisible,
  reserveData,
  phone,
  storageOptions,
  selectSize,
  selectedStorageSize,
  colours,
  selectedColour,
  onColourChange,
  availableOffers,
  variantLabels,
  inventoryLabels,
  page,
  reserveStatus,
  reservableCombos,
  phoneBrands,
}: Props) => {
  const { locale } = useIntl();
  const dispatch = useDispatch();
  // * Location Context Data
  const locationContext = useContext(StoreLocatorContext);

  const isReserveAccessoriesFFEnabled = useFeatureFlag(FeatureFlags.ReserveAccessories);

  const {
    origin,
    nearbyStores,
    allStoreLogos,
    setInputRef,
  } = locationContext;
  const { activeStore, setActiveStore } = useActiveStore();

  // CMS TEXT DATA
  // ============================
  // Get close modal text
  const closeModalSection = reserveData.phoneReservationSteps.find((item) => item.contentful_id === '731HmihXXgfqb46s2CIn0Q');
  // Step 1 text
  const step1Section = reserveData.phoneReservationSteps.find((item) => item.contentful_id === '5eDNWI9ub9DnD2jhmDBRbE');
  // Step 2 text
  const step2Section = reserveData.phoneReservationSteps.find((item) => item.contentful_id === 'g0lpatdLYXwc1SeLm46Rj');
  // Step 3 text
  const step3Section = reserveData.phoneReservationSteps.find((item) => item.contentful_id === '5mReYCzMjeXDjlpylQUtJd');
  // Step 3 Cart
  const step3SectionCart = reserveData.phoneReservationSteps.find(
    ({ contentful_id }) => contentful_id === '6zluneBdQyTEEQQCaLWQHQ',
  );
  // Step 4 text
  const step4Section = reserveData.phoneReservationSteps.find((item) => item.contentful_id === '4Iyh0ITfaGFDFeyTLJ0q1e');

  // PHASES OF BLADE
  // 1 = initial phase, with phone data and form for user to fill out
  // 2 = details confirmation, with button to submit request
  // 3 = response with confirmation code that request was recevied
  // 4 = error notiification, goes back to phase 1
  // 5 = Add to Cart success step
  // CloseModal = Displays and asks for a conf when a user tries to close the blade, before submitting a reservation
  // Initialize phase 1 as default
  const [phase, setPhase] = useState(1);

  /**
   * Set initial personal details state
   */

  const initialPersonalDetailsState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  };

  useEffect(() => {
    const bladeScrollContainer = document.querySelector(
      '.blade-scroll-container',
    );
    if (bladeScrollContainer) bladeScrollContainer.scrollTo(0, 0);
  }, [phase]);

  const [selectedSKU, setSelectedSKU] = useState(null);
  const [dppSelected, setDppSelected] = useState(false);
  const [dppResponse, setDppResponse] = useState(undefined);

  // This flag will trigger a useEffect in ReservationPhase1, to check and highlight any errors
  const [checkErrorTrigger, setCheckErrorTrigger] = useState(true);
  // Progress to phase 2 if user data is valid
  const handlePhase2 = () => {
    setCheckErrorTrigger(!checkErrorTrigger);
    if (
      chosenPhoneDetails
      && (isReserveAccessoriesFFEnabled || personalDetailsComplete)
      && selectedCarrierComplete
    ) {
      setSelectedSKU(selectedOffer?.sku);
      setPhase(2);
      setAdaMetaFields({
        reservationStatus: 'Incomplete',
      });
    }
    if (!selectedCarrierComplete) {
      setHasErrorCarrier(true);
    }

    if (!personalDetailsComplete) {
      setHasErrorPersonalDetails(true);
    }
  };

  // Allow user to go back to phase 1 to edit data, via "back" button
  const handlePrevPhase = () => {
    setPhase(1);
    setIsCaptchaChecked(false);
    setHasErrorCaptcha(false);
    setOptInStatus(false);
    setHasErrorOptIn(false);
  };

  // TRIGGER CLOSE MODAL
  const [closePromptVisible, setClosePromptVisible] = useState(false);

  // CLOSE MODAL RESPONSE
  // If "yes" close blade, which also resets user data
  // In "no" close modal and do nothing
  const closeBlade = (forceClose) => {
    if (!forceClose && phase !== 3) setClosePromptVisible(true);
    else {
      setClosePromptVisible(false);
      setBladeVisible(false);
      /** Reset state data when blade closes */
      setPhase(1);
      setPersonalDetails(initialPersonalDetailsState);
      setSelectedCarrier(undefined);
    }
  };

  const closeCartSuccessPhase = () => {
    dispatch(updateShowAddToCartTooltip(true));
    closeBlade(true);
  };

  // USER DATA FOR AXIOS SUBMISSION
  // Phone details from phone template page
  let chosenPhoneDetails = `${phone.displayName} ${selectedStorageSize} ${selectedColour}`;

  // Personal details from form
  const [personalDetails, setPersonalDetails] = useState(
    initialPersonalDetailsState,
  );
  const [personalDetailsComplete, setPersonalDetailsComplete] = useState(false);
  const [hasErrorPersonalDetails, setHasErrorPersonalDetails] = useState(false);

  // Update data from any input field updated
  // Keep other fields that have already been set
  const deliverFormField = (field, fieldValue) => {
    setPersonalDetails({ ...personalDetails, [field]: fieldValue });
  };

  // If all fields are updated, reset validation errors
  useEffect(() => {
    if (
      personalDetails.firstName
      && personalDetails.lastName
      && personalDetails.email
      && personalDetails.phone
    ) {
      setPersonalDetailsComplete(true);
      setHasErrorPersonalDetails(false);
    }
  }, [personalDetails]);

  // Put chosen carrier in state and set as complete
  const [selectedCarrier, setSelectedCarrier] = useState<
    ISelectedCarrier | undefined
  >();

  // Save meta fields for Ada chatbot
  const setAdaMetaFields = useCallback((metaFields = {}) => {
    onAdaReady((adaEmbed) => adaEmbed.setMetaFields({
      firstName: personalDetails?.firstName || '',
      lastName: personalDetails?.lastName || '',
      email: personalDetails?.email || '',
      phone: personalDetails?.phone || '',
      productName: chosenPhoneDetails || '',
      carrierName: selectedCarrier || '',
      selectedLocation: formatStoreName(activeStore),
      timestamp: Date.now(),
      ...metaFields,
    }));
  }, [
    activeStore,
    chosenPhoneDetails,
    personalDetails?.email,
    personalDetails?.firstName,
    personalDetails?.lastName,
    personalDetails?.phone,
    selectedCarrier,
  ]);

  const [selectedOffer, setSelectedOffer] = useState<Record<string, any>>();

  const [selectedCarrierComplete, setSelectedCarrierComplete] = useState(false);
  const [hasErrorCarrier, setHasErrorCarrier] = useState(false);
  const handleCarrierChange = (
    chosenCarrier,
    chosenCarrierImg,
    chosenCarrierUpfrontCost,
    chosenCarrierMonthlyCost,
    chosenCarrierPcoPoints,
    offer,
  ) => {
    setSelectedCarrier({
      carrierName: chosenCarrier,
      carrierImg: chosenCarrierImg,
      carrierUpfrontCost: chosenCarrierUpfrontCost,
      carrierMonthlyCost: chosenCarrierMonthlyCost,
      carrierPcoPoints: chosenCarrierPcoPoints,
    });
    setSelectedCarrierComplete(true);
    setHasErrorCarrier(false);
    setSelectedOffer(offer);
  };

  // Put chosen pick up location into state and set as complete

  const selectLocation = (store) => {
    setActiveStore(store);
  };

  // Reset carrier on colour change
  const selectColour = (colour) => {
    onColourChange(colour);
    setSelectedCarrier(undefined);
    setSelectedCarrierComplete(false);
    if (!reservableCombos[colour].includes(selectedStorageSize)) {
      selectSize(reservableCombos[colour][0]);
    }
  };
  // Reset carrier on size change
  const onSizeChange = (size) => {
    selectSize(size);
    setSelectedCarrier(undefined);
    setSelectedCarrierComplete(false);
  };
  // Put status of disclaimer checkbox into state and set as complete
  const [optInStatus, setOptInStatus] = useState(false);
  const [hasErrorOptIn, setHasErrorOptIn] = useState(false);
  const optInHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOptInStatus(e?.target.checked);
    if (e.target.checked) {
      setHasErrorOptIn(false);
    }
  };

  // Put status of captcha checkbox in state and set as complete
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);
  const [hasErrorCaptcha, setHasErrorCaptcha] = useState(false);
  const recaptchaRef = createRef();
  const captchaHandler = (status) => {
    setIsCaptchaChecked(status);
    if (status === true) {
      setHasErrorCaptcha(false);
    } else if (status === false) {
      setHasErrorCaptcha(true);
    }
  };

  // DATA SUBMISSION
  const [reservationData, setReservationData] = useState<Record<
    string,
    any
  > | null>(null);
  const { response, error, loading } = useTmsApi<number>(
    '/support/ticket',
    reservationData,
  );

  // Progress to phase 3 if disclaimer and captcha are vaild
  const handlePhase3 = useCallback(() => {
    if (!optInStatus && !isCaptchaChecked) {
      setHasErrorOptIn(true);
      setHasErrorCaptcha(true);
    } else if (!optInStatus) {
      setHasErrorOptIn(true);
    } else if (!isCaptchaChecked) {
      setHasErrorCaptcha(true);
    } else if (optInStatus && isCaptchaChecked) {
      // Send validated data to ZenDesk server
      const supportTicket = createSupportTicket({
        personalDetails,
        activeStore,
        dppSelected,
        dppResponse,
        chosenPhoneDetails,
        carrierName: selectedCarrier?.carrierName,
        recaptchaRef,
      });
      setReservationData(supportTicket);
    }
  }, [
    optInStatus,
    isCaptchaChecked,
    setReservationData,
    setHasErrorOptIn,
    setHasErrorCaptcha,
    selectedCarrier,
    activeStore,
    chosenPhoneDetails,
    dppResponse,
    dppSelected,
    personalDetails,
    recaptchaRef,
  ]);

  // Reservation success callback - called after successful reservation
  useEffect(() => {
    if (!response) return;

    // Send "reservation complete" meta fields to ADA chatbot
    setAdaMetaFields({
      reservationStatus: 'Complete',
    });

    // Push an ecommerce event to the GA dataLayer
    window.dataLayer = window.dataLayer || [];
    // Clear the previous ecommerce event (required)
    window.dataLayer.push({ ecommerce: null });
    // Send data about the current reservation
    window.dataLayer.push({
      ecommerce: {
        purchase: {
          actionField: {
            id: response,
            affiliation: 'The Mobile Shop Online Store',
            revenue: selectedCarrier?.carrierUpfrontCost || 0,
          },
          products: [
            {
              name: phone.displayName,
              id: selectedOffer?.sku || '',
              price: selectedCarrier?.carrierUpfrontCost || 0,
              variant: `${selectedCarrier} ${phone.displayName} ${selectedStorageSize} ${selectedColour}`,
              quantity: 1,
            },
          ],
        },
      },
    });

    // Navigate to reserve phase 3
    setConfCode(response);
    setPhase(3);
  }, [
    phone.displayName,
    response,
    selectedCarrier,
    selectedColour,
    selectedOffer?.sku,
    selectedStorageSize,
    setAdaMetaFields,
  ]);

  useEffect(() => {
    if (error) {
      setPhase(4);
    }
  }, [error]);

  // State for response from ZenDesk after data submission
  const [confCode, setConfCode] = useState<number | undefined>(undefined);

  useEffect(() => {
    setSelectedCarrier(undefined);
    setSelectedCarrierComplete(false);
  }, [origin]);

  const phase2BackButton = phase === 2 && (
    <PrevPhaseButton tabIndex={0} onClick={handlePrevPhase}>
      {reserveData.backLabel}
    </PrevPhaseButton>
  );

  const isPrepaidPhone = phone?.deviceType === DeviceType.PrepaidPhone;
  // TODO Refactor here
  return (
    <>
      <BladeContainer
        bladeState={bladeVisible}
        setBladeState={(state) => state ? setBladeVisible(true) : closeBlade(false)}
        addDivider
        bladeWidth={767}
        locale={locale}
        legendLeftButton={phase2BackButton}
        onClose={() => phase === 3 && isReserveAccessoriesFFEnabled
          ? dispatch(updateShowAddToCartTooltip(true))
          : undefined}
      >
        <Data>
          <DeviceInfoWrapper>
            {/* PHASE 1 - FORM */}
            {phase === 1 && (
              <ReservePhase1
                phone={phone}
                reserveData={step1Section}
                selectedColour={selectedColour}
                colours={colours}
                onColourChange={selectColour}
                selectSize={onSizeChange}
                selectedStorageSize={selectedStorageSize}
                storageOptions={storageOptions}
                deliverFormField={deliverFormField}
                handlePhase2={handlePhase2}
                selectLocation={selectLocation}
                selectedLocation={activeStore}
                variantLabels={variantLabels}
                availableOffers={availableOffers}
                selectedCarrier={selectedCarrier?.carrierName}
                handleCarrierChange={handleCarrierChange}
                hasErrorCarrier={hasErrorCarrier}
                personalDetails={personalDetails}
                hasErrorPersonalDetails={hasErrorPersonalDetails}
                nearbyStores={nearbyStores}
                allStoreLogos={allStoreLogos}
                setInputRef={setInputRef}
                inventoryLabels={inventoryLabels}
                page={page}
                checkErrorTrigger={checkErrorTrigger}
                setSelectedSKU={setSelectedSKU}
                selectedSKU={selectedSKU}
                reserveStatus={reserveStatus}
                showTradeIn={!isPrepaidPhone}
                showDisclaimer={!isPrepaidPhone}
                phoneBrands={phoneBrands}
                hasErrorLocation={false}
                isReserveAccessoriesFFEnabled={isReserveAccessoriesFFEnabled}
              />
            )}
            {/* PHASE 2 - CONF */}
            {phase === 2 && (
              <ReservePhase2
                reserveData={step2Section}
                handleSubmit={handlePhase3}
                chosenPhoneDetails={chosenPhoneDetails}
                phoneModel={phone.displayName}
                personalDetails={personalDetails}
                selectedCarrier={selectedCarrier}
                selectedStorageSize={selectedStorageSize}
                selectedColour={selectedColour}
                variantLabels={variantLabels}
                optInStatus={optInStatus}
                optInHandler={optInHandler}
                isCaptchaChecked={isCaptchaChecked}
                captchaHandler={captchaHandler}
                recaptchaRef={recaptchaRef}
                hasErrorCaptcha={hasErrorCaptcha}
                hasErrorOptIn={hasErrorOptIn}
                spinnerStatus={loading}
                buttonDisabled={loading}
                selectedLocation={activeStore}
                selectedSKU={selectedSKU}
                dppSelected={dppSelected}
                setDppSelected={setDppSelected}
                setDppResponse={setDppResponse}
                dppResponse={dppResponse}
                phone={phone}
                setPhase={setPhase}
                reserveStatus={reserveStatus}
                showDpp={!isPrepaidPhone}
                deviceType={phone.deviceType}
                showDisclaimer={!isPrepaidPhone}
                isReserveAccessoriesFFEnabled={isReserveAccessoriesFFEnabled}
              />
            )}
            {/* PHASE 3 - SUCCESS */}
            {phase === 3 && (
              isReserveAccessoriesFFEnabled ? (
                <CartSuccessPhase
                  phone={phone}
                  selectedColour={selectedColour}
                  selectedStorageSize={selectedStorageSize}
                  selectedCarrier={selectedCarrier}
                  variantLabels={variantLabels}
                  closeBlade={closeCartSuccessPhase}
                  cmsData={step3SectionCart}
                  showDisclaimer={!isPrepaidPhone}
                />
              ) : (
                <ReservePhase3
                  reserveData={step3Section}
                  confCode={confCode}
                  chosenPhoneDetails={chosenPhoneDetails}
                  phoneModel={phone.displayName}
                  carrierName={selectedCarrier?.carrierName}
                  selectedStorageSize={selectedStorageSize}
                  selectedColour={selectedColour}
                  closeBlade={closeBlade}
                  selectedLocation={activeStore}
                />
              )
            )}
            {/* PHASE 4 - ERROR */}
            {phase === 4 && (
              <ReserveError
                tryAgainHandler={handlePrevPhase}
                reserveData={step4Section}
              />
            )}
          </DeviceInfoWrapper>
        </Data>

        {/* CLOSE MODAL */}
      </BladeContainer>
      {closePromptVisible && (
        <CloseModal
          clickHandlerNo={() => setClosePromptVisible(false)}
          clickHandlerYes={() => closeBlade(true)}
          reserveData={closeModalSection}
        />
      )}
    </>
  );
};

export default ReservePhoneBlade;
